import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  MessageSquare, 
  BarChart3, 
  Settings, 
  Building2,
  X,
  Smartphone
} from 'lucide-react';
import { ROUTES } from '../../../utils/constants';
import useAuth from '../../../hooks/useAuth';

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.type === 'admin';
  const isTenant = user?.role === 'client' || user?.type === 'tenant';

  const menuItems = [
    {
      title: 'Visão Geral',
      icon: LayoutDashboard,
      path: ROUTES.OVERVIEW,
    },
    {
      title: 'Conversas',
      icon: MessageSquare,
      path: ROUTES.CONVERSATIONS,
    },
  ];

  // Adiciona menus de admin
  if (isAdmin) {
    menuItems.push(
      {
        title: 'Tenants',
        icon: Building2,
        path: ROUTES.TENANTS,
      },
      {
        title: 'Instâncias Evolution',
        icon: Smartphone,
        path: '/dashboard/evolution',
      }
    );
  }

  // Adiciona WhatsApp Onboarding apenas para tenant
  if (isTenant) {
    menuItems.push({
      title: 'Conectar WhatsApp',
      icon: Smartphone,
      path: '/onboarding/whatsapp',
    });
  }

  menuItems.push(
    {
      title: 'Relatórios',
      icon: BarChart3,
      path: '/dashboard/reports',
    },
    {
      title: 'Configurações',
      icon: Settings,
      path: ROUTES.SETTINGS,
    }
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen
          w-64 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo & Close button (mobile) */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:hidden">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Mara Labs" className="h-8 w-auto" />
              <h1 className="text-xl font-bold gradient-text font-display">
                Mara Labs
              </h1>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 overflow-y-auto custom-scrollbar">
            <div className="space-y-1">
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    // Fecha sidebar no mobile após clicar
                    if (window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-all duration-200
                    ${
                      isActive
                        ? 'bg-gradient-to-r from-primary-400 to-primary-500 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className="flex-1 font-medium">{item.title}</span>
                      {item.badge && (
                        <span
                          className={`
                            px-2 py-0.5 rounded-full text-xs font-semibold
                            ${
                              isActive
                                ? 'bg-white/20 text-white'
                                : 'bg-primary-100 text-primary-700'
                            }
                          `}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 text-sm mb-1">
                Precisa de ajuda?
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Entre em contato com nosso suporte
              </p>
              <button className="w-full px-3 py-1.5 bg-white text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-50 transition-colors">
                Falar com Suporte
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;